/**
 * Client-side AES-256-CBC, shared by the payload fields that must not travel
 * in plaintext: the login password (securePassword.js) and the signup OTP
 * (encryptOtp below).
 *
 * The scheme is the one the backend decrypts with, and has to match it
 * exactly: the 32-byte key is SHA-256 of a caller-supplied source string, the
 * IV is random per call, and the result is base64(iv || ciphertext) with the
 * IV as the first 16 bytes. Normalising the key source (trimming, lowercasing)
 * is the caller's job, because the backend's derivation for each field decides
 * what that string has to be.
 *
 * What this is and is not: TLS is the security boundary. Deriving the key from
 * a value the client already holds — and, for the OTP, one that travels in the
 * same request body — means anyone who can read the request can also decrypt
 * it. The point is that the raw secret is not sitting in plaintext wherever
 * that payload gets looked at afterwards: a network tab left open, a proxy
 * log, an error report that captured the request body.
 */
export async function encryptWithDerivedKey(plaintext, keySource) {
    const encoder = new TextEncoder()

    const keyMaterial = await window.crypto.subtle.digest('SHA-256', encoder.encode(String(keySource)))
    const key = await window.crypto.subtle.importKey('raw', keyMaterial, { name: 'AES-CBC' }, false, ['encrypt'])

    const iv = window.crypto.getRandomValues(new Uint8Array(16))
    const ciphertext = await window.crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, encoder.encode(String(plaintext)))

    const combined = new Uint8Array(iv.length + ciphertext.byteLength)
    combined.set(iv, 0)
    combined.set(new Uint8Array(ciphertext), iv.length)

    let binary = ''
    combined.forEach((byte) => {
        binary += String.fromCharCode(byte)
    })
    return window.btoa(binary)
}

/**
 * The signup verification code, encrypted for the one call that carries it
 * (auth/register/otp/verify). The key is derived from the registration id the
 * code belongs to, so a payload captured for one signup attempt cannot be
 * replayed into another — the server derives the same key from the
 * `registration_id` it was sent beside.
 *
 * Deliberately has no plaintext fallback: a browser that cannot do this cannot
 * verify, and quietly posting the bare code instead would defeat the whole
 * reason this exists. The caller surfaces the failure.
 */
export const encryptOtp = (otp, registrationId) =>
    encryptWithDerivedKey(otp, registrationId)

/**
 * The email-verification code (auth/email/otp/verify), keyed by the email it
 * was sent to — trimmed and lowercased, the same normalisation the backend
 * applies before deriving its key. Same no-plaintext-fallback rule as above.
 */
export const encryptEmailOtp = (otp, email) =>
    encryptWithDerivedKey(otp, String(email).trim().toLowerCase())
